import React from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { BabyChangingStation, CalendarMonth } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getLanguagePreferenceName } from '../i18n/languages';
import CitySearch from '../components/forms/CitySearch';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import NorthIndianChart from '../components/charts/NorthIndianChart';
import { useVedicYogas } from '../hooks/useVedicYogas';
import { formatDisplayDateTime } from '../utils/dateDisplay';
import i18n from '../i18n';
import { translateInline } from '../i18n/inlineTranslations';

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: { latitude: number; longitude: number };
  timezone: { name: string; offset: number };
  population: number;
  admin1?: { name: string; code?: string };
  country?: { name: string; code: string };
}

interface BirthWindow {
  start: string;
  end: string;
  durationMin: number;
  score: number;
  grade: string;
  positives: string[];
  cautions: string[];
  astrologySnapshot: {
    moonSign?: string | null;
    nakshatra?: string | null;
    tithi?: string | null;
    choghadiya?: string | null;
    hora?: string | null;
  };
}

type BirthIndicator = {
  name: string;
  nameHi: string;
  type: 'Dosha' | 'Yoga';
  status: 'Detected' | 'Not detected' | 'Review';
  tone: 'success' | 'warning' | 'info';
  summary: string;
  summaryHi: string;
};

const CLASSICAL_PLANETS = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN'];
const GAND_MOOL_NAKSHATRAS = new Set(['ASHWINI', 'ASHLESHA', 'MAGHA', 'JYESHTHA', 'MOOLA', 'MULA', 'REVATI']);
const PLANET_ALIASES: Record<string, string[]> = {
  SUN: ['SUN', 'Sun', 'Surya'],
  MOON: ['MOON', 'Moon', 'Chandra'],
  MARS: ['MARS', 'Mars', 'Mangal'],
  MERCURY: ['MERCURY', 'Mercury', 'Budh'],
  JUPITER: ['JUPITER', 'Jupiter', 'Guru'],
  VENUS: ['VENUS', 'Venus', 'Shukra'],
  SATURN: ['SATURN', 'Saturn', 'Shani'],
  RAHU: ['RAHU', 'Rahu'],
  KETU: ['KETU', 'Ketu']
};

const toYmd = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const shiftDate = (value: string, amount: number) => {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + amount);
  return toYmd(date);
};
const formatDateTime = (value: string) => formatDisplayDateTime(value, {
  timeZone: 'Asia/Kolkata',
});
const text = (_isHindi: boolean, en: string, hi: string) => translateInline(i18n.resolvedLanguage || i18n.language, en, hi);
const parseTime = (value: string) => {
  const [hour = '6', minute = '0'] = String(value || '06:00').split(':');
  return {
    hour: Number(hour) || 0,
    minute: Number(minute) || 0,
    second: 0
  };
};
const getLocalDateTimeParts = (isoValue: string, timeZone = 'Asia/Kolkata') => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone
  }).formatToParts(new Date(isoValue)).reduce<Record<string, string>>((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    dateOfBirth: `${parts.year}-${parts.month}-${parts.day}`,
    timeOfBirth: {
      hour: Number(parts.hour) || 0,
      minute: Number(parts.minute) || 0,
      second: 0
    }
  };
};

const normalizeToken = (value: any) => String(value || '').trim().toUpperCase().replace(/\s+/g, '_');
const getPlanet = (chart: any, key: string) => {
  const aliases = PLANET_ALIASES[key] || [key];
  for (const alias of aliases) {
    if (chart?.planets?.[alias]) return chart.planets[alias];
  }
  return Object.values(chart?.planets || {}).find((planet: any) => normalizeToken(planet?.name) === key);
};
const getSignNumber = (planet: any) => Number(planet?.sign?.number ?? planet?.signNumber ?? planet?.rashiNumber);
const getHouseNumber = (chart: any, planet: any) => {
  const direct = Number(planet?.d1House ?? planet?.rashiHouse ?? planet?.houseNumber ?? planet?.house ?? planet?.bhavaChalitHouse);
  if (Number.isFinite(direct) && direct >= 1 && direct <= 12) return direct;
  const ascSign = Number(chart?.houses?.ascendant?.sign?.number ?? chart?.ascendant?.sign?.number);
  const sign = getSignNumber(planet);
  if (Number.isFinite(ascSign) && Number.isFinite(sign)) return ((sign - ascSign + 12) % 12) + 1;
  return null;
};
const getLongitude = (planet: any) => {
  const longitude = Number(planet?.longitude);
  return Number.isFinite(longitude) ? ((longitude % 360) + 360) % 360 : null;
};
const isBetweenClockwise = (value: number, start: number, end: number) => (
  start <= end ? value >= start && value <= end : value >= start || value <= end
);
const allPlanetsBetween = (chart: any, start: number, end: number) => CLASSICAL_PLANETS.every((key) => {
  const longitude = getLongitude(getPlanet(chart, key));
  return longitude !== null && isBetweenClockwise(longitude, start, end);
});
const houseDistance = (fromHouse: number | null, toHouse: number | null) => {
  if (!fromHouse || !toHouse) return null;
  return ((toHouse - fromHouse + 12) % 12) + 1;
};
const sameSignOrHouse = (chart: any, first: any, second: any) => (
  Boolean(first && second) && (
    (Number.isFinite(getSignNumber(first)) && getSignNumber(first) === getSignNumber(second)) ||
    (getHouseNumber(chart, first) !== null && getHouseNumber(chart, first) === getHouseNumber(chart, second))
  )
);
const indicator = (name: string, nameHi: string, type: BirthIndicator['type'], detected: boolean, summary: string, summaryHi: string, review = false): BirthIndicator => ({
  name,
  nameHi,
  type,
  status: review ? 'Review' : detected ? 'Detected' : 'Not detected',
  tone: review ? 'info' : detected ? (type === 'Dosha' ? 'warning' : 'success') : 'info',
  summary,
  summaryHi
});
const detectBirthIndicators = (chart: any): BirthIndicator[] => {
  if (!chart?.planets) return [];
  const sun = getPlanet(chart, 'SUN');
  const moon = getPlanet(chart, 'MOON');
  const mars = getPlanet(chart, 'MARS');
  const mercury = getPlanet(chart, 'MERCURY');
  const jupiter = getPlanet(chart, 'JUPITER');
  const rahu = getPlanet(chart, 'RAHU');
  const ketu = getPlanet(chart, 'KETU');
  const saturn = getPlanet(chart, 'SATURN');
  const rahuLongitude = getLongitude(rahu);
  const ketuLongitude = getLongitude(ketu);
  const marsHouse = getHouseNumber(chart, mars);
  const moonHouse = getHouseNumber(chart, moon);
  const jupiterHouse = getHouseNumber(chart, jupiter);
  const moonNakshatra = normalizeToken(moon?.nakshatra?.name ?? moon?.nakshatraName);
  const manglikFromLagna = marsHouse !== null && [1, 4, 7, 8, 12].includes(marsHouse);
  const manglikFromMoon = houseDistance(moonHouse, marsHouse);
  const moonToJupiter = houseDistance(moonHouse, jupiterHouse);
  const kaalSarp = rahuLongitude !== null && ketuLongitude !== null
    ? allPlanetsBetween(chart, rahuLongitude, ketuLongitude) || allPlanetsBetween(chart, ketuLongitude, rahuLongitude)
    : false;

  return [
    indicator('Kaal Sarp Dosha', 'काल सर्प दोष', 'Dosha', kaalSarp, 'Checks whether the seven classical planets fall on one side of the Rahu-Ketu axis.', 'सात पारंपरिक ग्रह राहु-केतु अक्ष के एक तरफ हैं या नहीं, यह देखा जाता है।', rahuLongitude === null || ketuLongitude === null),
    indicator('Manglik Dosha', 'मांगलिक दोष', 'Dosha', manglikFromLagna || Boolean(manglikFromMoon && [1, 4, 7, 8, 12].includes(manglikFromMoon)), 'Mars is reviewed from Lagna and Moon for the 1st, 4th, 7th, 8th and 12th houses.', 'मंगल को लग्न और चंद्र से 1, 4, 7, 8 और 12वें भाव में देखा जाता है।', !mars),
    indicator('Gand Mool Dosha', 'गंड मूल दोष', 'Dosha', GAND_MOOL_NAKSHATRAS.has(moonNakshatra), 'Moon nakshatra is checked against Ashwini, Ashlesha, Magha, Jyeshtha, Moola and Revati.', 'चंद्र नक्षत्र को अश्विनी, अश्लेषा, मघा, ज्येष्ठा, मूल और रेवती से मिलाया जाता है।', !moonNakshatra),
    indicator('Pitru Dosha Indication', 'पितृ दोष संकेत', 'Dosha', sameSignOrHouse(chart, sun, rahu) || sameSignOrHouse(chart, sun, ketu), 'Sun connection with Rahu or Ketu is flagged for astrologer review.', 'सूर्य का राहु या केतु से संबंध समीक्षा के लिए दिखाया जाता है।', !sun || (!rahu && !ketu)),
    indicator('Guru Chandal Dosha', 'गुरु चांडाल दोष', 'Dosha', sameSignOrHouse(chart, jupiter, rahu) || sameSignOrHouse(chart, jupiter, ketu), 'Jupiter connection with Rahu or Ketu is checked.', 'गुरु का राहु या केतु से संबंध देखा जाता है।', !jupiter || (!rahu && !ketu)),
    indicator('Shani-Mangal Dosha', 'शनि-मंगल दोष', 'Dosha', sameSignOrHouse(chart, saturn, mars), 'Mars and Saturn conjunction by sign or house is checked.', 'मंगल और शनि का राशि या भाव से संबंध देखा जाता है।', !saturn || !mars),
    indicator('Budha-Aditya Yoga', 'बुध-आदित्य योग', 'Yoga', sameSignOrHouse(chart, sun, mercury), 'Sun and Mercury connection by sign or house is checked.', 'सूर्य और बुध का राशि या भाव से संबंध देखा जाता है।', !sun || !mercury),
    indicator('Chandra-Mangal Yoga', 'चंद्र-मंगल योग', 'Yoga', sameSignOrHouse(chart, moon, mars), 'Moon and Mars connection by sign or house is checked.', 'चंद्र और मंगल का राशि या भाव से संबंध देखा जाता है।', !moon || !mars),
    indicator('Gajakesari Yoga', 'गजकेसरी योग', 'Yoga', Boolean(moonToJupiter && [1, 4, 7, 10].includes(moonToJupiter)), 'Jupiter in a kendra from Moon is checked.', 'चंद्र से केंद्र में गुरु की स्थिति देखी जाती है।', !moon || !jupiter)
  ];
};

const ChildBirthMuhurat: React.FC = () => {
  const { i18n } = useTranslation();
  const isHindi = Boolean(i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi'));
  const { state: authState } = useAuth();
  const isPremium = hasPremiumSubscription(authState.user);
  const [selectedCity, setSelectedCity] = React.useState<City | null>(null);
  const [date, setDate] = React.useState(toYmd(new Date()));
  const [preferredStartTime, setPreferredStartTime] = React.useState('06:00');
  const [preferredEndTime, setPreferredEndTime] = React.useState('22:00');
  const [motherMoonSign, setMotherMoonSign] = React.useState('');
  const [windows, setWindows] = React.useState<BirthWindow[]>([]);
  const [rejectedSummary, setRejectedSummary] = React.useState<Record<string, number>>({});
  const [hasCalculatedWindows, setHasCalculatedWindows] = React.useState(false);
  const [kundliChart, setKundliChart] = React.useState<any | null>(null);
  const [kundliLabel, setKundliLabel] = React.useState('');
  const [kundliLoading, setKundliLoading] = React.useState(false);
  const [kundliLoadingKey, setKundliLoadingKey] = React.useState<string | null>(null);
  const [kundliError, setKundliError] = React.useState('');
  const [disclaimer, setDisclaimer] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess();
  const birthIndicators = React.useMemo(() => detectBirthIndicators(kundliChart), [kundliChart]);
  const { yogas, loading: yogasLoading, error: yogasError } = useVedicYogas(kundliChart, {
    enabled: isPremium && Boolean(kundliChart),
    token: authState.token
  });

  React.useEffect(() => {
    document.title = `${text(isHindi, 'Birth Mahurat', 'जन्म मुहूर्त')} - Kundlistar`;
  }, [isHindi]);

  React.useEffect(() => {
    setWindows([]);
    setRejectedSummary({});
    setHasCalculatedWindows(false);
  }, [date, selectedCity, preferredStartTime, preferredEndTime]);

  const buildChartPayload = (slotStart?: string) => {
    if (!selectedCity) throw new Error('Please select a city first.');
    const slotParts = slotStart ? getLocalDateTimeParts(slotStart, selectedCity.timezone.name || 'Asia/Kolkata') : null;
    const dateOfBirth = slotParts?.dateOfBirth || date;
    const timeOfBirth = slotParts?.timeOfBirth || parseTime(preferredStartTime);

    return {
      name: `Birth Mahurat ${dateOfBirth}`,
      dateOfBirth,
      timeOfBirth,
      placeOfBirth: {
        city: selectedCity.name,
        state: selectedCity.admin1?.name || '',
        country: selectedCity.country?.name || 'India',
        latitude: selectedCity.coordinates.latitude,
        longitude: selectedCity.coordinates.longitude,
        timezone: selectedCity.timezone.offset
      },
      gender: 'Other',
      saveChart: false,
      createdFrom: 'web',
      ayanamshaMode: 'LAHIRI',
      chartSettings: {
        chartStyle: 'North',
        language: getLanguagePreferenceName(i18n.resolvedLanguage || i18n.language)
      }
    };
  };

  const loadKundli = async (slotStart?: string) => {
    try {
      setKundliLoading(true);
      setKundliLoadingKey(slotStart || 'selected');
      setKundliError('');
      const response = await axios.post('/charts/calculate', buildChartPayload(slotStart));
      setKundliChart(response.data?.data?.chart || null);
      setKundliLabel(slotStart
        ? `${formatDateTime(slotStart)} in ${selectedCity?.displayName || selectedCity?.name}`
        : `${date} at ${preferredStartTime} in ${selectedCity?.displayName || selectedCity?.name}`);
    } catch (err: any) {
      setKundliError(err.response?.data?.message || err.message || 'Unable to load kundli.');
      setKundliChart(null);
    } finally {
      setKundliLoading(false);
      setKundliLoadingKey(null);
    }
  };

  const calculate = async () => {
    if (!selectedCity) {
      setError('Please select a city first.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = {
        startDate: date,
        endDate: date,
        birthPlace: {
          city: selectedCity.displayName || selectedCity.name,
          latitude: selectedCity.coordinates.latitude,
          longitude: selectedCity.coordinates.longitude,
          timezone: selectedCity.timezone.name || 'Asia/Kolkata',
          timezoneOffset: selectedCity.timezone.offset
        },
        slotIntervalMinutes: 15,
        deliveryType: 'planned_c_section',
        preferredStartTime,
        preferredEndTime,
        ...(motherMoonSign.trim() ? { motherMoonSign: motherMoonSign.trim() } : {})
      };

      await checkFeatureAccess({
        featureKey: 'CHILD_BIRTH_MUHURAT',
        apiCall: (trialAccessToken?: string) => axios.post('/child-birth-muhurat/calculate', payload, {
          headers: trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : undefined
        }),
        onSuccess: (response: any) => {
          const data = response.data || response;
          setDisclaimer(data.disclaimer || '');
          setWindows(data.bestWindows || []);
          setRejectedSummary(data.rejectedSummary || {});
          setHasCalculatedWindows(true);
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || text(isHindi, 'Unable to calculate Birth Mahurat.', 'जन्म मुहूर्त calculate नहीं हो सका।'));
      setWindows([]);
      setRejectedSummary({});
      setHasCalculatedWindows(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>{text(isHindi, 'Birth Mahurat | Kundlistar Premium', 'जन्म मुहूर्त | Kundlistar Premium')}</title>
        <meta name="description" content={text(isHindi, 'Birth Mahurat windows with Panchang, Choghadiya and Hora based guidance.', 'पंचांग, चौघड़िया और होरा आधारित जन्म मुहूर्त विंडो।')} />
      </Helmet>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          {text(isHindi, 'Birth Mahurat', 'जन्म मुहूर्त')}
        </Typography>
        <Typography color="text.secondary">
          {text(isHindi, 'Select a date and place to explore auspicious available windows for planned birth timing guidance.', 'जन्म समय मार्गदर्शन के लिए तारीख और स्थान चुनकर शुभ उपलब्ध विंडो देखें।')}
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        {text(isHindi, 'Birth Mahurat is for spiritual and astrological guidance only.', 'जन्म मुहूर्त केवल आध्यात्मिक और ज्योतिषीय मार्गदर्शन के लिए है।')}
      </Alert>

      {!isPremium ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
              <CalendarMonth color="primary" sx={{ fontSize: 42 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {text(isHindi, 'Upgrade to Kundlistar Premium', 'Kundlistar Premium अपग्रेड करें')}
                </Typography>
                <Typography color="text.secondary">
                  {text(isHindi, 'Birth Mahurat is available after choosing a Kundlistar Studio plan. Continue to unlock auspicious guidance windows with Panchang, Choghadiya and Hora checks.', 'जन्म मुहूर्त Kundlistar Studio plan चुनने के बाद उपलब्ध है। पंचांग, चौघड़िया और होरा checks के साथ शुभ guidance windows unlock करें।')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                href={authState.isAuthenticated ? '/upgrade' : '/login'}
              >
                {authState.isAuthenticated ? text(isHindi, 'Choose Plan', 'Premium अपग्रेड करें') : text(isHindi, 'Login to Upgrade', 'अपग्रेड के लिए लॉगिन करें')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' }, gap: 2 }}>
            <Box sx={{ gridColumn: { xs: '1', md: 'span 4' } }}>
              <TextField fullWidth type="date" label={text(isHindi, 'Date', 'तारीख')} value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: 'span 8' } }}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<CalendarMonth />} onClick={() => setDate(shiftDate(date, -1))}>{text(isHindi, 'Previous', 'पिछला')}</Button>
                <Button variant="outlined" onClick={() => setDate(toYmd(new Date()))}>{text(isHindi, 'Today', 'आज')}</Button>
                <Button variant="outlined" onClick={() => setDate(shiftDate(date, 1))}>{text(isHindi, 'Next', 'अगला')}</Button>
              </Box>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <CitySearch value={selectedCity} onChange={setSelectedCity} label={text(isHindi, 'Birth place / City', 'जन्म स्थान / शहर')} placeholder={text(isHindi, 'Search city...', 'शहर खोजें...')} required />
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: 'span 4' } }}>
              <TextField fullWidth type="time" label={text(isHindi, 'Preferred start', 'पसंदीदा शुरुआत')} value={preferredStartTime} onChange={(event) => setPreferredStartTime(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: 'span 4' } }}>
              <TextField fullWidth type="time" label={text(isHindi, 'Preferred end', 'पसंदीदा समाप्ति')} value={preferredEndTime} onChange={(event) => setPreferredEndTime(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: 'span 4' } }}>
              <TextField fullWidth label={text(isHindi, 'Mother Moon sign (optional)', 'माता की चंद्र राशि (वैकल्पिक)')} value={motherMoonSign} onChange={(event) => setMotherMoonSign(event.target.value)} placeholder={text(isHindi, 'Cancer', 'कर्क')} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="large" startIcon={<CalendarMonth />} onClick={() => loadKundli()} disabled={kundliLoading}>
                  {kundliLoading ? <CircularProgress size={22} color="inherit" /> : text(isHindi, 'Load Kundli', 'कुंडली लोड करें')}
                </Button>
                <Button variant="contained" size="large" startIcon={<BabyChangingStation />} onClick={calculate} disabled={loading || trialLoading}>
                  {loading || trialLoading ? <CircularProgress size={22} color="inherit" /> : text(isHindi, 'Find Premium Windows', 'प्रीमियम विंडो देखें')}
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}
      {kundliError ? <Alert severity="error" sx={{ mb: 3 }}>{kundliError}</Alert> : null}

      {kundliChart ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {text(isHindi, 'Kundli Preview', 'कुंडली प्रीव्यू')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {text(isHindi, 'Loaded for', 'लोड किया गया')} {kundliLabel || `${date} ${preferredStartTime} ${selectedCity?.displayName || selectedCity?.name}`}.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
              <NorthIndianChart chartData={kundliChart} size={420} showHouseLabels />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {text(isHindi, 'Doshas & Yogas', 'दोष और योग')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {text(isHindi, 'Rule-based indications from the loaded Lahiri kundli. Use them for review and spiritual guidance, not as a final judgement.', 'लोड की गई लाहिड़ी कुंडली से rule-based संकेत। इन्हें review और आध्यात्मिक मार्गदर्शन के रूप में देखें।')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
                {birthIndicators.map((item) => (
                  <Box key={item.name} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{text(isHindi, item.name, item.nameHi)}</Typography>
                      <Chip size="small" color={item.tone} label={text(isHindi, item.status, item.status === 'Detected' ? 'मिला' : item.status === 'Not detected' ? 'नहीं मिला' : 'समीक्षा')} />
                    </Box>
                    <Chip size="small" variant="outlined" label={text(isHindi, item.type, item.type === 'Dosha' ? 'दोष' : 'योग')} sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">{text(isHindi, item.summary, item.summaryHi)}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {text(isHindi, 'Full Yoga Scan', 'पूर्ण योग स्कैन')}
              </Typography>
              {yogasLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">{text(isHindi, 'Checking yogas...', 'योग जांचे जा रहे हैं...')}</Typography>
                </Box>
              ) : yogasError ? (
                <Alert severity="warning" sx={{ mb: 1 }}>{yogasError}</Alert>
              ) : yogas.length ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                  {yogas.map((yoga) => (
                    <Box key={yoga.id || yoga.name} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>{yoga.name}</Typography>
                        <Chip size="small" color={yoga.isPositive ? 'success' : 'warning'} label={yoga.status || yoga.strength || 'Yoga'} />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                        {yoga.type ? <Chip size="small" variant="outlined" label={yoga.type} /> : null}
                        {yoga.category ? <Chip size="small" variant="outlined" label={yoga.category} /> : null}
                        {yoga.strength ? <Chip size="small" variant="outlined" label={yoga.strength} /> : null}
                      </Box>
                      {yoga.formation ? <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{yoga.formation}</Typography> : null}
                      {(yoga.effects || []).slice(0, 2).map((effect) => (
                        <Typography key={effect} variant="body2" color="text.secondary">• {effect}</Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">{text(isHindi, 'No major yogas were detected by the premium yoga scan.', 'प्रीमियम योग स्कैन में कोई प्रमुख योग नहीं मिला।')}</Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {hasCalculatedWindows && !loading && windows.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {text(isHindi, 'No suitable premium windows found for the selected date and time range.', 'चुनी गई तारीख और समय सीमा में suitable premium windows नहीं मिलीं।')}
          </Typography>
          {Object.keys(rejectedSummary).length ? (
            <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
              {Object.entries(rejectedSummary).map(([reason, count]) => (
                <li key={reason}>
                  {reason} ({count} slots)
                </li>
              ))}
            </Box>
          ) : (
            <Typography variant="body2">{text(isHindi, 'Try changing the time range or select previous/next date.', 'समय सीमा बदलें या पिछली/अगली तारीख चुनें।')}</Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {text(isHindi, 'Try the Previous or Next date button to find a better option.', 'बेहतर विकल्प के लिए पिछला या अगला तारीख बटन इस्तेमाल करें।')}
          </Typography>
        </Alert>
      ) : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
        {windows.map((item, index) => (
          <Box key={`${item.start}-${index}`}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatDateTime(item.start)}</Typography>
                    <Typography color="text.secondary">{formatDateTime(item.end)} · {item.durationMin} min</Typography>
                  </Box>
                  <Chip label={`${item.score}/100 ${item.grade}`} color={item.score >= 80 ? 'success' : 'primary'} />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {item.astrologySnapshot.tithi ? <Chip size="small" label={item.astrologySnapshot.tithi} /> : null}
                  {item.astrologySnapshot.nakshatra ? <Chip size="small" label={item.astrologySnapshot.nakshatra} /> : null}
                  {item.astrologySnapshot.choghadiya ? <Chip size="small" label={`${item.astrologySnapshot.choghadiya} Choghadiya`} /> : null}
                  {item.astrologySnapshot.hora ? <Chip size="small" label={`${item.astrologySnapshot.hora} Hora`} /> : null}
                </Box>
                {item.positives.slice(0, 4).map((copy) => (
                  <Typography key={copy} variant="body2" sx={{ mb: 0.5 }}>• {copy}</Typography>
                ))}
                {item.cautions.slice(0, 3).map((copy) => (
                  <Typography key={copy} variant="body2" color="warning.main" sx={{ mb: 0.5 }}>• {copy}</Typography>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarMonth />}
                  onClick={() => loadKundli(item.start)}
                  disabled={kundliLoading}
                  sx={{ mt: 1.5 }}
                >
                  {kundliLoadingKey === item.start ? <CircularProgress size={18} color="inherit" /> : text(isHindi, 'Load Kundli', 'कुंडली लोड करें')}
                </Button>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {!hasCalculatedWindows && !windows.length && !loading ? (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {text(isHindi, 'Click Find Premium Windows to calculate the best premium windows for the selected date and city.', 'चुनी गई तारीख और शहर के लिए best premium windows देखने हेतु Premium Windows देखें पर क्लिक करें।')}
        </Typography>
      ) : null}
        </>
      )}

      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading}
        onCancel={closePopup}
        onContinue={popup.nextAction}
      />
    </Container>
  );
};

export default ChildBirthMuhurat;
